/**
 * SUB-4.5 · FR40, FR41. The navigator's two reads, and the only two places the
 * pipeline navigator learns what is in scope.
 *
 * ⚠️ THE BOUND IS THE FUNCTION'S, NOT THIS FILE'S (ISO4). Both calls resolve
 * `merged_scope_tenant_ids()` inside the database, which carries SUB-4.8's role
 * gate, mode gate and per-element revalidation. Nothing here filters rows, and
 * nothing here may ever become the reason a pipeline is absent.
 *
 * ⚠️ CALLED UNCONDITIONALLY, NOT ONLY IN MERGED SCOPE. `merged_scope_tenant_ids()`
 * returns exactly `ARRAY[get_current_user_tenant_id()]` for every caller who is
 * not in merged scope, so these RPCs answer identically to the tenant-pinned
 * table reads they replace. One path that is right in both modes beats two paths
 * that agree until someone edits one of them, which is the trap SUB-4.2 recorded
 * on `taskService` and `pipelinePaginationService` in the same week.
 *
 * ⚠️ FAILURE NARROWS, NEVER WIDENS. Every fallback below is the tenant-pinned
 * table read this replaced, so an unapplied migration, a revoked grant or a
 * flaky request leaves the caller with their own account rather than with
 * nothing or with somebody else's rows. `degraded` reports that it happened;
 * TELLING THE USER is NFR8 and belongs to SUB-4.10, which owns the whole
 * "narrowed silently" surface and extends `classifyScope` for it.
 */

import { supabase } from '@/lib/supabaseClient';
import type { LineagePipeline } from '@/lib/pipelineLineage';

/** A pipeline row as the navigator needs it. The table row is a superset. */
export interface ScopePipeline extends LineagePipeline {
  id: number;
  name: string;
  pipeline_type: string | null;
  tenant_id: string | null;
  cloned_from_pipeline_id: number | null;
  role_permissions?: unknown;
  user_permissions?: unknown;
  team_permissions?: unknown;
  priority?: number | null;
}

/**
 * A stage row as the board needs it. The index signature is deliberate: the RPC
 * returns `SETOF stages`, and the board hands the whole row on to components
 * that read columns this module has no business enumerating.
 */
export interface ScopeStage {
  id: number;
  name: string;
  pipeline_id: number;
  stage_order: number | null;
  tenant_id: string | null;
  [column: string]: unknown;
}

export interface ScopeRead<T> {
  rows: T[];
  /**
   * True when the merged read could not be used and the single-account fallback
   * answered instead. The rows are correct and narrower; they are not merged.
   */
  degraded: boolean;
}

/** The one place the fallback's account is resolved, so it cannot drift. */
async function currentTenantId(): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_current_user_tenant_id');
  if (error || typeof data !== 'string') return null;
  return data;
}

/**
 * FR40. Every pipeline in the caller's current scope, with the two columns
 * grouping needs on every row: `tenant_id` for attribution and
 * `cloned_from_pipeline_id` for lineage.
 */
export async function fetchScopePipelines(): Promise<ScopeRead<ScopePipeline>> {
  const { data, error } = await supabase.rpc('merged_scope_pipelines');

  if (!error && Array.isArray(data)) {
    return { rows: data as ScopePipeline[], degraded: false };
  }

  const tenantId = await currentTenantId();
  if (!tenantId) return { rows: [], degraded: true };

  const { data: rows, error: fallbackError } = await supabase
    .from('pipelines')
    .select('id, name, pipeline_type, tenant_id, cloned_from_pipeline_id, priority, role_permissions, user_permissions, team_permissions')
    .eq('tenant_id', tenantId)
    .order('name');

  if (fallbackError || !rows) return { rows: [], degraded: true };
  return { rows: rows as unknown as ScopePipeline[], degraded: true };
}

/**
 * FR41 · AC3. One pipeline's OWN stages.
 *
 * ⚠️ THERE IS NO MERGED FORM OF THIS CALL AND THERE MUST NOT BE. The AC says
 * each account tab shows that account's own stages, "never merged, reconciled or
 * normalized". Asking by `pipeline_id` is what guarantees it: a pipeline belongs
 * to exactly one account, so a merge is not something this path can express.
 *
 * Returns EVERY stage, archived ones included. The RPC deliberately does not
 * apply the board's `_archived_%` exclusion: which stages a surface draws is
 * that surface's decision, and a read helper that silently dropped rows would
 * make two callers disagree about what a pipeline's stages are. `fetchBoardStages`
 * below is the board's own answer.
 */
export async function fetchPipelineStages(
  pipelineId: number | null | undefined,
): Promise<ScopeRead<ScopeStage>> {
  if (pipelineId == null) return { rows: [], degraded: false };

  const { data, error } = await supabase.rpc('merged_scope_pipeline_stages', {
    p_pipeline_id: pipelineId,
  });

  if (!error && Array.isArray(data)) {
    return { rows: data as ScopeStage[], degraded: false };
  }

  const { data: rows, error: fallbackError } = await supabase
    .from('stages')
    .select('*')
    .eq('pipeline_id', pipelineId)
    .order('stage_order', { ascending: true });

  if (fallbackError || !rows) return { rows: [], degraded: true };
  return { rows: rows as unknown as ScopeStage[], degraded: true };
}

/**
 * The board's own stage list: in scope, not archived, in stage order.
 *
 * The archived-name convention is the product's, and it is applied here rather
 * than in the database so that the RPC keeps answering "these are the pipeline's
 * stages" and this function answers "these are the ones the board draws columns
 * for".
 *
 * ⚠️ THE REGEX REPRODUCES SQL `LIKE '_archived_%'`, NOT `startsWith`. In LIKE,
 * `_` is a single-character wildcard, so the board's existing exclusion drops any
 * name of the form <any char>archived<any char>… The obvious JS translation is
 * stricter than the query it replaces, and a stage the board used to hide
 * reappearing is exactly the kind of difference that gets blamed on merged scope.
 */
const ARCHIVED_STAGE_NAME = /^.archived./s;

export async function fetchBoardStages(
  pipelineId: number | null | undefined,
): Promise<ScopeRead<ScopeStage>> {
  const read = await fetchPipelineStages(pipelineId);
  return {
    ...read,
    rows: read.rows.filter((row) => {
      const name = typeof row.name === 'string' ? row.name : '';
      return !ARCHIVED_STAGE_NAME.test(name);
    }),
  };
}
